import { createSlice } from '@reduxjs/toolkit';
import initialState from './initialState';
import {
  addAgent,
  addCustomField,
  blockDateFullDays,
  blockDateRangeDay,
  cancelVisit,
  closeCalendar,
  deleteSelectedExceptions,
  deleteSpecificException,
  getCalendarStatus,
  getCalendarConfig,
  getEventTypesList,
  getTimeline,
  getVisitEvents,
  modifyAvailability,
  openCalendar,
  refreshExceptions,
  removeAgent,
  removeExtraField,
  selectSchedule,
  setVisitDuration,
  toggleParticipant,
  toggleVisitField,
  getPropertyInfo,
  postCreateMessageContact,
  deleteEvent,
  addContact,
  removeContact,
  deletePropertyConfiguration,
  getVisitData,
  confirmVisit,
} from './services';
import { toISOStringWithGMT } from '@/components/PropertiesVisit/utils/dates';
import getHourToMinutes from '@/components/PropertiesVisit/utils/getHourToMinutes';
import {
  parseExtraAgents,
  parseExtraContacts,
  parseFixedParticipants,
} from '@/components/PropertiesVisit/utils/parseParticipants';

const isVisitNotificationModalOpen = (state) =>
  state.generalCalendar.isCancelVisitModalOpen ||
  state.generalCalendar.isConfirmVisitModalOpen;

const clearSelectedVisit = (state) => {
  state.generalCalendar.selectedVisitToCancel =
    initialState.generalCalendar.selectedVisitToCancel;
};

const closeCancelVisitModal = (state) => {
  state.generalCalendar.isCancelVisitModalOpen = false;
  state.generalCalendar.cancelVisitOptions =
    initialState.generalCalendar.cancelVisitOptions;
};

const closeConfirmVisitModal = (state) => {
  state.generalCalendar.isConfirmVisitModalOpen = false;
  state.generalCalendar.confirmVisitOptions =
    initialState.generalCalendar.confirmVisitOptions;
};

export const PropertiesVisitSlice = createSlice({
  name: 'PropertiesVisit',
  initialState,
  reducers: {
    setPropertiesVisitData(state, action) {
      const { key, subkey, value } = action.payload;

      if (subkey) {
        state[key][subkey] = value;
      } else {
        state[key] = value;
      }
    },
    updateVisitField(state, action) {
      const { key, subkey, value } = action.payload;

      if (subkey) {
        state.generalCalendar.visitFields[key][subkey] = value;
      } else {
        state.generalCalendar.visitFields[key] = value;
      }
    },
    toggleEnableCalendarModal(state) {
      state.generalCalendar.enableCalendarModal.isOpen =
        !state.generalCalendar.enableCalendarModal.isOpen;
    },
    toggleDisableCalendarModal(state) {
      state.generalCalendar.disableCalendarModal.isOpen =
        !state.generalCalendar.disableCalendarModal.isOpen;

      state.generalCalendar.selectedTabId =
        initialState.generalCalendar.selectedTabId;

      state.generalCalendar.disableCalendarCheckboxes =
        initialState.generalCalendar.disableCalendarCheckboxes;
    },
    toggleDisableCalendarCheckbox(state, action) {
      const field = action.payload;
      const currentValue =
        state.generalCalendar.disableCalendarCheckboxes[field];

      state.generalCalendar.disableCalendarCheckboxes[field] = !currentValue;
    },
    toggleVisitDetailModal(state, action) {
      const visitId = action.payload;
      const foundVisit = state.generalCalendar.allVisits.find(
        (visit) => visit.id === visitId
      );

      state.generalCalendar.selectedVisitToDetail = state.generalCalendar
        .isVisitDetailModalOpen
        ? null
        : foundVisit;
      state.generalCalendar.isVisitDetailModalOpen =
        !state.generalCalendar.isVisitDetailModalOpen;
    },
    toggleCancelVisitModal(state, action) {
      const { payload } = action;
      const visitId =
        payload && typeof payload === 'object' ? payload.visitId : payload;
      const scheduleStatus =
        payload && typeof payload === 'object'
          ? payload.scheduleStatus
          : undefined;

      if (state.generalCalendar.isCancelVisitModalOpen) {
        closeCancelVisitModal(state);
        clearSelectedVisit(state);
        return;
      }

      closeConfirmVisitModal(state);
      clearSelectedVisit(state);
      state.generalCalendar.cancelVisitOptions.visitId = visitId;
      state.generalCalendar.cancelVisitOptions.loadingVisitData = true;
      state.generalCalendar.cancelVisitOptions.scheduleStatus =
        scheduleStatus ?? null;
      state.generalCalendar.isCancelVisitModalOpen = true;
    },
    toggleConfirmVisitModal(state, action) {
      const visitId = action.payload;

      if (state.generalCalendar.isConfirmVisitModalOpen) {
        closeConfirmVisitModal(state);
        clearSelectedVisit(state);
        return;
      }

      closeCancelVisitModal(state);
      clearSelectedVisit(state);
      state.generalCalendar.confirmVisitOptions.visitId = visitId;
      state.generalCalendar.confirmVisitOptions.loadingVisitData = true;
      state.generalCalendar.isConfirmVisitModalOpen = true;
    },
    toggleCancelNonGuidedVisitModal(state, action) {
      const visitId = action.payload;

      if (!visitId) {
        state.generalCalendar.selectedVisitToCancel =
          initialState.generalCalendar.selectedVisitToCancel;
      } else {
        state.generalCalendar.cancelVisitOptions.visitId = visitId;
      }

      state.generalCalendar.cancelNonGuidedVisitModal.isOpen =
        !state.generalCalendar.cancelNonGuidedVisitModal.isOpen;
    },
    toggleBlockedDate(state, action) {
      const id = action.payload;

      state.generalCalendar.blockedDates =
        state.generalCalendar.blockedDates.map((blockedDate) =>
          blockedDate.id === id
            ? { ...blockedDate, checked: !blockedDate.checked }
            : blockedDate
        );
    },
    addSpecificBlockedDateToDelete(state, action) {
      const id = action.payload;
      const foundDate = state.generalCalendar.blockedDates.find(
        (date) => date.id === id
      );

      if (!foundDate) return;

      state.generalCalendar.specificBlockedDateToDelete = foundDate;
      state.generalCalendar.deleteBlockedDatesModal.isOpen = true;
    },
    addBlockedFullDatesToDelete(state) {
      const filtered = state.generalCalendar.blockedDates.filter(
        (blockedDate) => blockedDate.checked
      );

      state.generalCalendar.blockedDatesToDelete = filtered;
      state.generalCalendar.deleteBlockedDatesModal.isOpen = true;
    },
    toggleAllBlockedDates(state) {
      const { blockedDates } = state.generalCalendar;
      // Consider only selectable (non-company) exceptions for tri-state logic
      const selectable = blockedDates.filter((date) => !date.companyConfig);
      const allChecked =
        selectable.length > 0 && selectable.every((date) => date.checked);
      const allUnchecked = selectable.every((date) => !date.checked);
      const justAFewAreChecked = !allChecked && !allUnchecked;

      // When partially checked or all unchecked -> check all selectable
      // When all selectable are checked -> uncheck all selectable
      const newCheckedValue = justAFewAreChecked || allUnchecked;

      state.generalCalendar.blockedDates = blockedDates.map((blockedDate) => {
        if (blockedDate.companyConfig) {
          // Never mark company-config exceptions as checked
          return { ...blockedDate, checked: false };
        }
        return { ...blockedDate, checked: newCheckedValue };
      });
    },
    updateCancelVisitOption(state, action) {
      const { key, value } = action.payload;
      state.generalCalendar.cancelVisitOptions[key] = value;
    },
    updateConfirmVisitOption(state, action) {
      const { key, value } = action.payload;
      state.generalCalendar.confirmVisitOptions[key] = value;
    },
    updateFilters(state, action) {
      const { key, value } = action.payload;
      state.generalCalendar.filters[key] = value;
      const isNonFetchRelevantChange = key === 'date' && value !== 'custom';

      if (isNonFetchRelevantChange) {
        state.generalCalendar.filters.customDates =
          initialState.generalCalendar.filters.customDates;
      } else {
        state.generalCalendar.paginator.page = 1;
      }
    },
    updateParticipants(state, action) {
      const { key, value } = action.payload;
      state.generalCalendar.participants[key] = value;
    },
    parseExceptions(state, action) {
      let currentId = 0;

      function toParsedTime(str) {
        return str?.substring(0, 5) || '';
      }

      function parseSingleException(exception) {
        const {
          date,
          start_date,
          end_date,
          start_time,
          end_time,
          group_id,
          company_config,
          exceptions: nestedExceptions = [],
        } = exception;

        const from = toParsedTime(start_time);
        const to = toParsedTime(end_time);

        const isGroup = Boolean(group_id);

        const parsed = {
          id: currentId++,
          hourFrom: from,
          hourTo: to,
          checked: false,
          companyConfig: company_config,
          exceptions: nestedExceptions.map(parseSingleException),
        };

        if (isGroup) {
          parsed.groupId = group_id;
          parsed.dayFrom = toISOStringWithGMT(start_date);
          parsed.dayTo = toISOStringWithGMT(end_date);
        } else {
          parsed.dayFrom = toISOStringWithGMT(date);
        }

        return parsed;
      }

      function flattenExceptions(exceptions) {
        return exceptions.flatMap((exception) =>
          exception.groupId
            ? flattenExceptions(exception.exceptions || [])
            : [exception]
        );
      }

      const { exceptions, exception_groups } = action.payload;

      const formattedExceptions = exceptions.map((exception) =>
        parseSingleException(exception)
      );
      const formattedGroups = exception_groups.map((exception) =>
        parseSingleException(exception)
      );

      const allParsed = [...formattedExceptions, ...formattedGroups].sort(
        (a, b) => new Date(a.dayFrom) - new Date(b.dayFrom)
      );

      const allFlatExceptions = flattenExceptions(allParsed);

      state.generalCalendar.blockedDates = allParsed;
      state.generalCalendar.allBlockedDates = allFlatExceptions;
    },
    toggleAddContactModal(state, action) {
      if (!state.generalCalendar.addContactModal.isOpen) {
        state.generalCalendar.addContactModal.isOpen = true;
        state.generalCalendar.addContactForm.name = {
          value: action.payload || '',
        };
        return;
      }

      state.generalCalendar.addContactModal = {
        isLoading: false,
        isOpen: false,
      };
      state.generalCalendar.addContactForm =
        initialState.generalCalendar.addContactForm;
    },
    updateAddContactForm(state, action) {
      const { key, value } = action.payload;
      let updatedField = {};

      if (key === 'cellphone') {
        updatedField = {
          ...state.generalCalendar.addContactForm.cellphone,
          value,
          isValidated: false,
          isError: false,
        };
      } else {
        updatedField = { value };
      }

      state.generalCalendar.addContactForm[key] = {
        ...state.generalCalendar.addContactForm[key],
        ...updatedField,
      };
    },
    updateCellphoneStatusContactForm(state, action) {
      const {
        valid: isValid,
        validated: isValidated,
        error: isError,
        loading: isLoading,
      } = action.payload;

      state.generalCalendar.addContactForm.cellphone = {
        ...state.generalCalendar.addContactForm.cellphone,
        isValid,
        isValidated,
        isError,
        isLoading,
      };
    },
    setCalendarConfig(state, action) {
      const { data, isCompany, propertyId } = action.payload;

      const days = {
        0: 'monday',
        1: 'tuesday',
        2: 'wednesday',
        3: 'thursday',
        4: 'friday',
        5: 'saturday',
        6: 'sunday',
      };
      const daysInOrder = [0, 1, 2, 3, 4, 5, 6];

      const {
        config_day_hours,
        minute_duration_schedule_time,
        participants_info,
        prevent_calendar_overlap,
        superposition_type,
        is_same_company_config,
        contact_info = [],
        custom_contact_info_fields = [],
        exceptions = [],
        exception_groups = [],
        visits_require_approval,
        gap_between_visits_minutes,
        gap_between_visits_required,
      } = data;
      const defaultRanges = [
        {
          from: { timelapse: getHourToMinutes('09:00'), value: '09:00' },
          to: { timelapse: getHourToMinutes('18:00'), value: '18:00' },
        },
      ];
      const parsedConfigDayHours = {};
      Object.entries(config_day_hours).forEach(([day, ranges]) => {
        const parsedNumberDay = Number(day);
        parsedConfigDayHours[parsedNumberDay] = ranges;
      });

      const noOrderCustomSchedule = Object.entries(days).map((day) => {
        const [dayNumber] = day;
        const mappedDay = {
          id: Number(dayNumber),
          active: Boolean(parsedConfigDayHours[dayNumber]),
          ranges: parsedConfigDayHours[dayNumber]
            ? parsedConfigDayHours[dayNumber].map((range) => {
                const fromValue = range.start.substring(0, 5);
                const fromTimelapse = getHourToMinutes(fromValue);
                const toValue = range.end.substring(0, 5);
                const toTimelapse = getHourToMinutes(toValue);

                const from = { timelapse: fromTimelapse, value: fromValue };
                const to = { timelapse: toTimelapse, value: toValue };

                return {
                  from,
                  to,
                };
              })
            : defaultRanges,
        };

        return mappedDay;
      });

      const orderedCustomSchedule = daysInOrder.map((day) =>
        noOrderCustomSchedule.find((el) => el.id === day)
      );

      PropertiesVisitSlice.caseReducers.parseExceptions(state, {
        type: 'PropertiesVisit/parseExceptions',
        payload: { exceptions, exception_groups },
      });

      let durations = [...initialState.generalCalendar.durations];
      const foundDuration = durations.find(
        (duration) => duration === minute_duration_schedule_time
      );

      if (!foundDuration) {
        durations = [...durations, minute_duration_schedule_time].sort();
      }

      const selectedDuration = foundDuration || durations[0];

      const fixedParticipants = parseFixedParticipants({
        producer: participants_info.producer,
        propertyAgent: participants_info.property_agent,
        contactAgent: participants_info.contact_agent,
        owner: participants_info.owner,
        doorman: participants_info.doorman,
        keyAgent: participants_info.key_agent,
      });
      const extraAgents = parseExtraAgents(participants_info.extra_agents);

      const participants = {
        ...fixedParticipants,
        preventCalendarOverlap: prevent_calendar_overlap,
        superpositionType: superposition_type,
        isModifyingAvailability: false,
        agents: extraAgents,
        extraContacts: [],
      };

      if (!isCompany) {
        const extraContacts = parseExtraContacts(
          participants_info?.extra_contacts || []
        );

        participants.extraContacts = extraContacts;
      }

      // name is always needed and required
      const visitConfigMap = {
        email: {
          needed: contact_info.email_needed,
          required: contact_info.email_required,
        },
        phone: {
          needed: contact_info.phone_needed,
          required: contact_info.phone_required,
        },
        comment: {
          needed: contact_info.comment_needed,
          required: contact_info.comment_required,
        },
      };

      const fixedFields = state.generalCalendar.visitFields.fixedFields.map(
        (field) => {
          const config = visitConfigMap[field.id];
          return config ? { ...field, ...config } : field;
        }
      );

      const extraFields = custom_contact_info_fields.map((extraField) => ({
        id: extraField.custom_field_name,
        text: extraField.custom_field_name,
        needed: extraField.custom_field_needed,
        required: extraField.custom_fields_required,
        disabled: false,
        requiredDisabled: false,
        isCustom: true,
        isTextarea: false,
      }));

      state.generalCalendar.customSchedule = orderedCustomSchedule;
      state.generalCalendar.visitFields = {
        ...state.generalCalendar.visitFields,
        fixedFields,
        extraFields,
      };

      state.generalCalendar.isDifferentConfigurationBannerOpen =
        !isCompany && !is_same_company_config;
      state.generalCalendar.isSameCompanyConfig = is_same_company_config;
      state.generalCalendar.participants = participants;
      state.generalCalendar.loadingExceptions = false;
      state.generalCalendar.durations = durations;
      state.generalCalendar.selectedDuration = selectedDuration;
      state.generalCalendar.isLoadingConfig = false;
      state.generalCalendar.isFilledConfig = true;

      state.generalCalendar.isCompany = isCompany;
      state.generalCalendar.propertyId = propertyId;
      state.generalCalendar.visitsRequireApproval = visits_require_approval;

      state.generalCalendar.gapBetweenVisitsMinutes =
        gap_between_visits_minutes;
      state.generalCalendar.gapBetweenVisitsRequired =
        gap_between_visits_required;
    },
    handleDifferentConfiguration(state) {
      const { isCompany } = state.generalCalendar;

      const isSameConfig = isCompany;

      state.generalCalendar.isDifferentConfigurationBannerOpen = !isSameConfig;
      state.generalCalendar.isSameCompanyConfig = isSameConfig;
    },
    resetPropertiesVisitData(state) {
      // Reset all data to initial state
      state.generalCalendar = {
        ...initialState.generalCalendar,
      };
    },
  },
  extraReducers: (builder) => {
    // getCalendarConfig
    builder.addCase(getCalendarConfig.pending, (state) => {
      state.generalCalendar.isLoadingConfig = true;
      state.generalCalendar.isFilledConfig = false;
    });
    builder.addCase(getCalendarConfig.fulfilled, (state, action) => {
      const data = action.payload;
      const isCompany = !action.meta.arg.propertyId;
      const propertyId = isCompany ? null : action.meta.arg.propertyId;

      PropertiesVisitSlice.caseReducers.setCalendarConfig(state, {
        type: 'PropertiesVisit/setCalendarConfig',
        payload: { data, isCompany, propertyId },
      });
    });
    builder.addCase(getCalendarConfig.rejected, () => {
      console.error('get config failed');
    });

    // getCalendarStatus
    builder.addCase(getCalendarStatus.pending, (state) => {
      state.generalCalendar.isLoadingStatus = true;
      state.generalCalendar.isFilledStatus = false;
    });
    builder.addCase(getCalendarStatus.fulfilled, (state, action) => {
      // 0: closed
      // 1: active
      const activeStatus = 1;
      const mappedScheduleKeys = {
        'Mon to Fri': 1,
        Saturday: 2,
        Custom: 3,
      };
      state.generalCalendar.isOpen = action.payload.status === activeStatus;
      state.generalCalendar.isDisabledCalendarBannerOpen =
        action.payload.status !== activeStatus;

      state.generalCalendar.schedule.key =
        mappedScheduleKeys[action.payload.calendar_configuration_type];
      state.generalCalendar.isLoadingStatus = false;
      state.generalCalendar.isFilledStatus = true;
    });
    builder.addCase(getCalendarStatus.rejected, () => {
      console.error('get calendar status by company failed');
    });

    // openCalendar
    builder.addCase(openCalendar.pending, (state) => {
      state.generalCalendar.enableCalendarModal.isLoading = true;
    });
    builder.addCase(openCalendar.fulfilled, (state) => {
      state.generalCalendar.isOpen = true;
      state.generalCalendar.enableCalendarModal.isLoading = false;
      state.generalCalendar.enableCalendarModal.isOpen = false;
      state.generalCalendar.isDisabledCalendarBannerOpen = false;

      PropertiesVisitSlice.caseReducers.handleDifferentConfiguration(state, {
        type: 'PropertiesVisit/handleDifferentConfiguration',
      });
    });
    builder.addCase(openCalendar.rejected, (state) => {
      state.generalCalendar.enableCalendarModal.isLoading = false;
    });

    // closeCalendar
    builder.addCase(closeCalendar.pending, (state) => {
      state.generalCalendar.disableCalendarModal.isLoading = true;
    });
    builder.addCase(closeCalendar.fulfilled, (state) => {
      state.generalCalendar.isOpen = false;
      state.generalCalendar.disableCalendarModal.isLoading = false;
      state.generalCalendar.disableCalendarModal.isOpen = false;
      state.generalCalendar.disableCalendarCheckboxes =
        initialState.generalCalendar.disableCalendarCheckboxes;

      PropertiesVisitSlice.caseReducers.handleDifferentConfiguration(state, {
        type: 'PropertiesVisit/handleDifferentConfiguration',
      });
    });
    builder.addCase(closeCalendar.rejected, (state) => {
      state.generalCalendar.disableCalendarModal.isLoading = false;
    });

    // blockDateFullDays
    builder.addCase(blockDateFullDays.pending, (state) => {
      state.generalCalendar.blockDateFullDaysModal.isLoading = true;
    });
    builder.addCase(blockDateFullDays.fulfilled, (state) => {
      state.generalCalendar.blockDateFullDaysModal.isLoading = false;
      state.generalCalendar.blockDateFullDaysModal.isOpen = false;
      state.generalCalendar.selectedBlockedFullDates =
        initialState.generalCalendar.selectedBlockedFullDates;

      PropertiesVisitSlice.caseReducers.handleDifferentConfiguration(state, {
        type: 'PropertiesVisit/handleDifferentConfiguration',
      });
    });
    builder.addCase(blockDateFullDays.rejected, (state) => {
      state.generalCalendar.blockDateFullDaysModal.isLoading = false;
    });

    // blockDateRangeDay
    builder.addCase(blockDateRangeDay.pending, (state) => {
      state.generalCalendar.blockDateRangeDayModal.isLoading = true;
    });
    builder.addCase(blockDateRangeDay.fulfilled, (state) => {
      state.generalCalendar.selectedBlockedRangeDate = null;
      state.generalCalendar.blockDateRangeDayModal.isLoading = false;
      state.generalCalendar.blockDateRangeDayModal.isOpen = false;

      PropertiesVisitSlice.caseReducers.handleDifferentConfiguration(state, {
        type: 'PropertiesVisit/handleDifferentConfiguration',
      });
    });
    builder.addCase(blockDateRangeDay.rejected, (state) => {
      state.generalCalendar.blockDateRangeDayModal.isLoading = false;
    });

    // refreshExceptions
    builder.addCase(refreshExceptions.pending, (state) => {
      state.generalCalendar.loadingExceptions = true;
    });
    builder.addCase(refreshExceptions.fulfilled, (state, action) => {
      const { exceptions = [], exception_groups = [] } = action.payload;
      state.generalCalendar.loadingExceptions = false;

      PropertiesVisitSlice.caseReducers.parseExceptions(state, {
        type: 'PropertiesVisit/parseExceptions',
        payload: { exceptions, exception_groups },
      });
    });
    builder.addCase(refreshExceptions.rejected, () => {
      console.log('error getting exceptions!');
    });

    // selectSchedule
    builder.addCase(selectSchedule.pending, (state) => {
      state.generalCalendar.schedule.isLoading = true;
    });
    builder.addCase(selectSchedule.fulfilled, (state) => {
      state.generalCalendar.schedule.isLoading = false;

      PropertiesVisitSlice.caseReducers.handleDifferentConfiguration(state, {
        type: 'PropertiesVisit/handleDifferentConfiguration',
      });
    });
    builder.addCase(selectSchedule.rejected, (state) => {
      state.generalCalendar.schedule.isLoading = false;
    });

    // setVisitDuration
    builder.addCase(setVisitDuration.pending, () => {
      // ...
    });
    builder.addCase(setVisitDuration.fulfilled, (state) => {
      PropertiesVisitSlice.caseReducers.handleDifferentConfiguration(state, {
        type: 'PropertiesVisit/handleDifferentConfiguration',
      });
    });
    builder.addCase(setVisitDuration.rejected, () => {
      // ...
    });

    // deleteSpecificException
    builder.addCase(deleteSpecificException.pending, (state) => {
      state.generalCalendar.deleteBlockedDatesModal.isLoading = true;
    });
    builder.addCase(deleteSpecificException.fulfilled, (state) => {
      const filteredBlockedDates = state.generalCalendar.blockedDates.filter(
        (date) =>
          date.id !== state.generalCalendar.specificBlockedDateToDelete.id
      );

      state.generalCalendar.blockedDates = filteredBlockedDates;
      state.generalCalendar.specificBlockedDateToDelete = null;
      state.generalCalendar.deleteBlockedDatesModal.isLoading = false;
      state.generalCalendar.deleteBlockedDatesModal.isOpen = false;

      PropertiesVisitSlice.caseReducers.handleDifferentConfiguration(state, {
        type: 'PropertiesVisit/handleDifferentConfiguration',
      });
    });
    builder.addCase(deleteSpecificException.rejected, (state) => {
      state.generalCalendar.deleteBlockedDatesModal.isLoading = false;
    });

    // deleteSelectedExceptions
    builder.addCase(deleteSelectedExceptions.pending, (state) => {
      state.generalCalendar.deleteBlockedDatesModal.isLoading = true;
    });
    builder.addCase(deleteSelectedExceptions.fulfilled, (state) => {
      state.generalCalendar.blockedDatesToDelete =
        initialState.generalCalendar.blockedDatesToDelete;

      state.generalCalendar.deleteBlockedDatesModal.isLoading = false;
      state.generalCalendar.deleteBlockedDatesModal.isOpen = false;

      PropertiesVisitSlice.caseReducers.handleDifferentConfiguration(state, {
        type: 'PropertiesVisit/handleDifferentConfiguration',
      });
    });
    builder.addCase(deleteSelectedExceptions.rejected, (state) => {
      state.generalCalendar.deleteBlockedDatesModal.isLoading = false;
    });

    // getEventTypesList
    builder.addCase(getEventTypesList.pending, (state) => {
      state.generalCalendar.eventTypes.loading = true;
    });
    builder.addCase(getEventTypesList.fulfilled, (state, action) => {
      state.generalCalendar.eventTypes.list = action.payload;
      state.generalCalendar.eventTypes.loading = false;
    });
    builder.addCase(getEventTypesList.rejected, (state) => {
      state.generalCalendar.eventTypes.loading = false;
    });

    // getVisitEvents
    builder.addCase(getVisitEvents.pending, (state, action) => {
      state.generalCalendar.loadingVisits = true;
      state.generalCalendar.latestVisitEventsRequestId = action.meta.requestId;
    });
    builder.addCase(getVisitEvents.fulfilled, (state, action) => {
      // Ignore stale responses from a superseded request (e.g. the
      // unfiltered mount fetch resolving after a filter was applied).
      if (
        action.meta.requestId !==
        state.generalCalendar.latestVisitEventsRequestId
      ) {
        return;
      }

      // ...
      const rawVisits = action.payload?.results?.events || {};
      const parsedVisits = {};
      const allVisits = [];

      Object.entries(rawVisits).forEach(([dateKey, visits]) => {
        visits.forEach((visit) => {
          const {
            visit_id,
            scheduled_timezone,
            contact,
            property,
            contacts_rested,
            properties_rested,
            is_self_guided,
          } = visit;

          const id = visit_id;

          const date = dateKey;
          const timezone = scheduled_timezone;

          const hour = {
            from: visit.start_hour,
            to: null,
          };

          const statusId = visit.schedule_status;

          const modified = {
            by: visit.modified_by,
            date: visit.modified_date,
            hour: visit.modified_hour,
          };

          const data = {
            id,
            timezone,
            contact,
            modified,
            property,
            hour,
            date,
            statusId,
            contacts_rested,
            properties_rested,
            is_self_guided,
          };

          allVisits.push(data);
          if (parsedVisits[dateKey]) {
            parsedVisits[dateKey].push(data);
          } else {
            parsedVisits[dateKey] = [data];
          }
        });
      });

      state.generalCalendar.paginator.count = action.payload.count;
      state.generalCalendar.groupedVisits = parsedVisits;
      state.generalCalendar.allVisits = allVisits;
      state.generalCalendar.loadingVisits = false;
    });
    builder.addCase(getVisitEvents.rejected, () => {
      console.log('error fetching visits!');
    });

    // cancelVisit
    builder.addCase(cancelVisit.pending, (state) => {
      state.generalCalendar.isCancelingVisit = true;
    });
    builder.addCase(cancelVisit.fulfilled, (state) => {
      state.generalCalendar.isCancelingVisit = false;
      closeCancelVisitModal(state);
      clearSelectedVisit(state);
    });
    builder.addCase(cancelVisit.rejected, (state) => {
      state.generalCalendar.isCancelingVisit = false;
    });

    builder.addCase(confirmVisit.pending, (state) => {
      state.generalCalendar.isConfirmingVisit = true;
    });
    builder.addCase(confirmVisit.fulfilled, (state) => {
      state.generalCalendar.isConfirmingVisit = false;
      closeConfirmVisitModal(state);
      clearSelectedVisit(state);
    });
    builder.addCase(confirmVisit.rejected, (state) => {
      state.generalCalendar.isConfirmingVisit = false;
    });

    // getTimeline
    builder.addCase(getTimeline.pending, (state) => {
      state.generalCalendar.selectedVisitToDetail.isRetrievingTimeline = true;
      state.generalCalendar.selectedVisitToDetail.timeline = null;
    });
    builder.addCase(getTimeline.fulfilled, (state, action) => {
      const timeline = action.payload.map((singleTimelineItem) => ({
        ...singleTimelineItem,
        action: Number(singleTimelineItem.action),
      }));

      state.generalCalendar.selectedVisitToDetail.isRetrievingTimeline = false;
      state.generalCalendar.selectedVisitToDetail.timeline = timeline;
    });
    builder.addCase(getTimeline.rejected, () => {
      console.log('error retrieving timeline!');
    });

    // toggleParticipant
    builder.addCase(toggleParticipant.pending, () => {
      // ...
    });
    builder.addCase(toggleParticipant.fulfilled, (state) => {
      PropertiesVisitSlice.caseReducers.handleDifferentConfiguration(state, {
        type: 'PropertiesVisit/handleDifferentConfiguration',
      });
    });
    builder.addCase(toggleParticipant.rejected, () => {
      console.log('error toggling participant!');
    });

    // addAgent
    builder.addCase(addAgent.pending, (state, action) => {
      const { extra_agents } = action.meta.arg;

      state.generalCalendar.participants.agents.forEach((agent) => {
        if (extra_agents.includes(agent.id)) {
          agent.loading = true;
        }
      });
    });

    builder.addCase(addAgent.fulfilled, (state, action) => {
      const { extra_agents } = action.meta.arg;

      state.generalCalendar.participants.agents.forEach((agent) => {
        if (extra_agents.includes(agent.id)) {
          agent.loading = false;
        }
      });

      PropertiesVisitSlice.caseReducers.handleDifferentConfiguration(state, {
        type: 'PropertiesVisit/handleDifferentConfiguration',
      });
    });

    builder.addCase(addAgent.rejected, (state, action) => {
      console.log('error adding agents!');
      const { extra_agents } = action.meta.arg;

      state.generalCalendar.participants.agents =
        state.generalCalendar.participants.agents.filter(
          (agent) => !extra_agents.includes(agent.id)
        );
    });

    // removeAgent
    builder.addCase(removeAgent.pending, (state, action) => {
      const { extra_agents } = action.meta.arg;

      state.generalCalendar.participants.agents.forEach((agent) => {
        if (extra_agents.includes(agent.id)) {
          agent.loading = true;
        }
      });
    });

    builder.addCase(removeAgent.fulfilled, (state, action) => {
      const { extra_agents } = action.meta.arg;

      state.generalCalendar.participants.agents =
        state.generalCalendar.participants.agents.filter(
          (agent) => !extra_agents.includes(agent.id)
        );

      PropertiesVisitSlice.caseReducers.handleDifferentConfiguration(state, {
        type: 'PropertiesVisit/handleDifferentConfiguration',
      });
    });

    builder.addCase(removeAgent.rejected, (state, action) => {
      console.log('error removing agents!');
      const { extra_agents } = action.meta.arg;

      state.generalCalendar.participants.agents.forEach((agent) => {
        if (extra_agents.includes(agent.id)) {
          agent.loading = false;
        }
      });
    });

    // addContact
    builder.addCase(addContact.pending, (state, action) => {
      const { extra_contacts } = action.meta.arg;

      state.generalCalendar.participants.extraContacts.forEach((contact) => {
        if (extra_contacts.includes(contact.id)) {
          contact.loading = true;
        }
      });
    });

    builder.addCase(addContact.fulfilled, (state, action) => {
      const { extra_contacts } = action.meta.arg;

      state.generalCalendar.addContactModal = {
        isOpen: false,
        isLoading: false,
      };
      state.generalCalendar.participants.extraContacts.forEach((contact) => {
        if (extra_contacts.includes(contact.id)) {
          contact.loading = false;
        }
      });
    });

    builder.addCase(addContact.rejected, (state, action) => {
      console.log('error adding contacts!');
      const { extra_contacts } = action.meta.arg;

      state.generalCalendar.participants.extraContacts =
        state.generalCalendar.participants.extraContacts.filter(
          (contact) => !extra_contacts.includes(contact.id)
        );
    });

    // removeContact
    builder.addCase(removeContact.pending, (state, action) => {
      const { extra_contacts } = action.meta.arg;

      state.generalCalendar.participants.extraContacts.forEach((contact) => {
        if (extra_contacts.includes(contact.id)) {
          contact.loading = true;
        }
      });
    });

    builder.addCase(removeContact.fulfilled, (state, action) => {
      const { extra_contacts } = action.meta.arg;

      state.generalCalendar.participants.extraContacts =
        state.generalCalendar.participants.extraContacts.filter(
          (contact) => !extra_contacts.includes(contact.id)
        );
    });

    builder.addCase(removeContact.rejected, (state, action) => {
      console.log('error removing contacts!');
      const { extra_contacts } = action.meta.arg;

      state.generalCalendar.participants.extraContacts.forEach((contact) => {
        if (extra_contacts.includes(contact.id)) {
          contact.loading = false;
        }
      });
    });

    // modifyAvailability
    builder.addCase(modifyAvailability.pending, (state) => {
      state.generalCalendar.participants.isModifyingAvailability = true;
    });
    builder.addCase(modifyAvailability.fulfilled, (state) => {
      state.generalCalendar.participants.isModifyingAvailability = false;

      PropertiesVisitSlice.caseReducers.handleDifferentConfiguration(state, {
        type: 'PropertiesVisit/handleDifferentConfiguration',
      });
    });
    builder.addCase(modifyAvailability.rejected, () => {
      console.log('error modifying availability!');
    });

    // toggleVisitField
    builder.addCase(toggleVisitField.pending, () => {
      // ...
    });
    builder.addCase(toggleVisitField.fulfilled, (state) => {
      PropertiesVisitSlice.caseReducers.handleDifferentConfiguration(state, {
        type: 'PropertiesVisit/handleDifferentConfiguration',
      });
    });
    builder.addCase(toggleVisitField.rejected, () => {
      console.log('error toggling a visit field!');
    });

    // getPropertyInfo
    builder.addCase(getPropertyInfo.pending, () => {
      // ...
    });
    builder.addCase(getPropertyInfo.fulfilled, (state, action) => {
      const { can_edit, data } = action.payload;
      const { id, address, location, status } = data;
      const code = action.payload.data.reference;
      const thumbnail =
        action.payload.data.pictures?.front_cover_image?.url ||
        action.payload.data.pictures?.images[0];

      const propertyInfo = {
        id,
        thumbnail,
        code,
        address,
        location,
        status,
        can_edit,
      };

      state.generalCalendar.propertyInfo = propertyInfo;
    });
    builder.addCase(getPropertyInfo.rejected, () => {
      console.log('error getting property info!');
    });

    // addCustomField
    builder.addCase(addCustomField.pending, (state) => {
      state.generalCalendar.visitFields.isAddingExtraField = true;
    });
    builder.addCase(addCustomField.fulfilled, (state, action) => {
      state.generalCalendar.visitFields.isAddingExtraField = false;
      state.generalCalendar.visitFields.isInputOpen = false;
      state.generalCalendar.visitFields.inputValue = '';

      const newField = {
        id: action.meta.arg.custom_contact_info_fields[0].custom_field_name,
        text: action.meta.arg.custom_contact_info_fields[0].custom_field_name,
        needed: true,
        required: false,
        disabled: false,
        requiredDisabled: false,
        isCustom: true,
        isTextarea: false,
      };

      state.generalCalendar.visitFields.extraFields = [
        ...state.generalCalendar.visitFields.extraFields,
        newField,
      ];

      PropertiesVisitSlice.caseReducers.handleDifferentConfiguration(state, {
        type: 'PropertiesVisit/handleDifferentConfiguration',
      });
    });
    builder.addCase(addCustomField.rejected, (state) => {
      console.log('error adding custom field!');
      state.generalCalendar.visitFields.isAddingExtraField = false;
    });

    builder.addCase(removeExtraField.pending, (state, action) => {
      const { custom_contact_info_fields } = action.meta.arg;

      state.generalCalendar.visitFields.extraFields.forEach((extraField) => {
        if (
          custom_contact_info_fields.some(
            (field) => field.custom_field_name === extraField.id
          )
        ) {
          extraField.loading = true;
        }
      });
    });

    builder.addCase(removeExtraField.fulfilled, (state, action) => {
      const { custom_contact_info_fields } = action.meta.arg;

      state.generalCalendar.visitFields.extraFields =
        state.generalCalendar.visitFields.extraFields.filter(
          (extraField) =>
            !custom_contact_info_fields.some(
              (field) => field.custom_field_name === extraField.id
            )
        );

      PropertiesVisitSlice.caseReducers.handleDifferentConfiguration(state, {
        type: 'PropertiesVisit/handleDifferentConfiguration',
      });
    });

    builder.addCase(removeExtraField.rejected, (state, action) => {
      console.log('error removing an extra visit field!');
      const { custom_contact_info_fields } = action.meta.arg;

      state.generalCalendar.visitFields.extraFields.forEach((extraField) => {
        if (
          custom_contact_info_fields.some(
            (field) => field.custom_field_name === extraField.id
          )
        ) {
          extraField.loading = false;
        }
      });
    });

    builder // postCreateMessageContact
      .addCase(postCreateMessageContact.pending, (state) => {
        state.isCancelingVisit = true;
      })
      .addCase(postCreateMessageContact.fulfilled, (state) => {
        state.isCancelingVisit = false;
      });

    // deleteEvent
    builder.addCase(deleteEvent.pending, (state) => {
      state.generalCalendar.cancelNonGuidedVisitModal.isLoading = true;
    });
    builder.addCase(deleteEvent.fulfilled, (state) => {
      state.generalCalendar.cancelNonGuidedVisitModal.isLoading = false;
    });

    // deletePropertyConfiguration
    builder.addCase(deletePropertyConfiguration.pending, (state) => {
      state.generalCalendar.isLoadingConfig = true;
      state.generalCalendar.isFilledConfig = false;
      state.generalCalendar.isLoadingStatus = true;
    });
    builder.addCase(deletePropertyConfiguration.fulfilled, (state, action) => {
      const data = action.payload;
      const { isCompany, propertyId } = state.generalCalendar;

      PropertiesVisitSlice.caseReducers.setCalendarConfig(state, {
        type: 'PropertiesVisit/setCalendarConfig',
        payload: { data, isCompany, propertyId },
      });
    });
    builder.addCase(deletePropertyConfiguration.rejected, () => {
      console.error('delete property configuration failed');
    });

    // getVisitData
    builder.addCase(getVisitData.pending, (state, action) => {
      state.generalCalendar.latestVisitDataRequestId = action.meta.requestId;

      if (state.generalCalendar.isCancelVisitModalOpen) {
        state.generalCalendar.cancelVisitOptions.loadingVisitData = true;
      }

      if (state.generalCalendar.isConfirmVisitModalOpen) {
        state.generalCalendar.confirmVisitOptions.loadingVisitData = true;
      }
    });
    builder.addCase(getVisitData.fulfilled, (state, action) => {
      if (
        action.meta.requestId !== state.generalCalendar.latestVisitDataRequestId
      ) {
        return;
      }

      if (!isVisitNotificationModalOpen(state)) {
        return;
      }

      if (state.generalCalendar.isCancelVisitModalOpen) {
        state.generalCalendar.cancelVisitOptions.loadingVisitData = false;
      }

      if (state.generalCalendar.isConfirmVisitModalOpen) {
        state.generalCalendar.confirmVisitOptions.loadingVisitData = false;
      }

      const {
        id,
        date,
        time,
        scheduled_timezone,
        property_id,
        property_photo,
        address,
        string_location,
        contact,
        comment,
        schedule_status,
      } = action.payload;

      state.generalCalendar.selectedVisitToCancel = {
        id,
        date,
        hour: {
          from: time,
        },
        timezone: scheduled_timezone,
        property: {
          id: property_id,
          image: property_photo,
          address,
          location_string: string_location,
        },
        contact,
        comment: comment ?? null,
        schedule_status,
      };
    });
    builder.addCase(getVisitData.rejected, (state, action) => {
      if (action.meta.aborted || action.meta.condition) {
        return;
      }

      if (
        action.meta.requestId !== state.generalCalendar.latestVisitDataRequestId
      ) {
        return;
      }

      if (state.generalCalendar.isCancelVisitModalOpen) {
        closeCancelVisitModal(state);
      }

      if (state.generalCalendar.isConfirmVisitModalOpen) {
        closeConfirmVisitModal(state);
      }

      clearSelectedVisit(state);
    });
  },
});

export const {
  setPropertiesVisitData,
  updateVisitField,
  toggleEnableCalendarModal,
  toggleDisableCalendarModal,
  toggleDisableCalendarCheckbox,
  toggleBlockedDate,
  toggleVisitDetailModal,
  toggleCancelVisitModal,
  toggleConfirmVisitModal,
  toggleCancelNonGuidedVisitModal,
  addSpecificBlockedDateToDelete,
  addBlockedFullDatesToDelete,
  toggleAllBlockedDates,
  updateCancelVisitOption,
  updateConfirmVisitOption,
  updateFilters,
  updateParticipants,
  toggleAddContactModal,
  updateAddContactForm,
  resetPropertiesVisitData,
  updateCellphoneStatusContactForm,
} = PropertiesVisitSlice.actions;

export default PropertiesVisitSlice.reducer;
